import { getApiClient } from '../api_client/get';
import { Container } from 'unstated';
import { getPersistentStore, getPersistentStoreMutex } from '@lemonadefashion/storage';
import { Seller } from '@lemonadefashion/seller';

const KEY = 'sellers_info';
function _getStore() { return getPersistentStore(); }
function _getMutex() { return getPersistentStoreMutex(); }

// Note:
// The keys sellers and sellers_timestamp were used
// before to represent data for this container. Becareful when
// using them at a future date, since they will contain essentially old trash.

// We used to store the list and timestamp separately,
// but it's better for correctness that the list and timestamp
// are coupled to ensure atomicity.
function getSellersInfoFromStore() {
  try {
    const sellersInfoPayloadJson = _getStore().getItem(KEY);
    if (sellersInfoPayloadJson == null) return { sellers: [], timestamp: 0 };
    return JSON.parse(sellersInfoPayloadJson);
  } catch (error) {
    console.log(error);
    return { sellers: [], timestamp: 0 };
  }
}

function getSellersFromStore() {
  const payload = getSellersInfoFromStore();
  try {
    return {
      sellers: payload.sellers.map(x => new Seller(x)),
      timestamp: payload.timestamp
    };
  } catch (error) {
    console.log(error);
    return { sellers: [], timestamp: 0 };
  }
}

function updateSellersInfoInStore(sellers, timestamp) {
  return new Promise((resolve, reject) => {
    const store = _getStore();
    const mutex = _getMutex();
    mutex.lock(KEY)
      .then(() => {
        store.setItem(KEY, JSON.stringify({ sellers: sellers, timestamp: timestamp }));
        return mutex.release(KEY).then(resolve).catch(reject);
      })
      .catch(reject);
  });
}

class SellersContainer extends Container {
  constructor() {
    super();
    this.state = { ...this._getUpdateFromStore(), ready: false };
    window.addEventListener('storage', (e) => {
      if (e.key !== KEY) return;
      this.setState(this._getUpdateFromStore());
    });
  }

  // This must be called while the app is being initialized.
  initialize() {
    if (this.shouldGoToNetwork()) {
      return this.loadFromNetwork();
    } else {
      return new Promise((resolve, reject) => { this.setState({ ready: true }, resolve); });
    }
  }

  // Any sellers data older than 10 minutes is stale.
  shouldGoToNetwork() {
    if (this.state.sellers == null) return true;
    if (this.state.timestamp == null) return true;
    const ageSeconds = (Date.now() - this.state.timestamp) / 1000;
    return ageSeconds > 600;
  }

  isReady() {
    return this.state.ready;
  }

  _getUpdateFromStore() {
    const info = getSellersFromStore();
    return { sellers: info.sellers, timestamp: info.timestamp };
  }

  loadFromNetwork() {
    return new Promise((resolve, reject) => {
      getApiClient().getSellers()
        .then(response => {
          updateSellersInfoInStore(response.data.sellers, Date.now())
            .catch(console.log)
            .finally(() => this.setState({ ...this._getUpdateFromStore(), ready: true }, resolve));
        })
        .catch(reject);
    });
  }

  // Only to be used in tests/mocks
  set(sellers) {
    return new Promise((resolve, reject) => {
      updateSellersInfoInStore(sellers, Date.now())
        .catch(console.log)
        .finally(() => this.setState(this._getUpdateFromStore(), resolve));
    });
  }

  getAll() {
    if (this.state.sellers && this.state.sellers.length) return this.state.sellers;
    return [];
  }

  getBySlug(slug) {
    if (this.state.sellers && this.state.sellers.length) {
      const filtered = this.state.sellers.filter(x => x.getSlug() === slug);
      if (filtered.length) return filtered[0];
    }
    return null;
  }
}

export default SellersContainer;
